import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';
import { Theme } from '../../ui/theme';

const LANES = ['D', 'F', 'J', 'K'] as const;

// Layout — 4 lanes × 160px each = 640px total, centered on 1440px screen
const LANE_WIDTH = 160;
const LANE_TOTAL_W = LANE_WIDTH * 4;                         // 640
const LANE_X_START = (GAME_WIDTH - LANE_TOTAL_W) / 2;       // 400 — left edge of lane area

const HIT_LINE_Y = GAME_HEIGHT - 140;
const NOTE_FALL_DURATION = 3800; // ms for note to travel from top to hit line — 충분히 천천히
const SONG_DURATION_MS = 30_000;
const PERFECT_WINDOW = 110;
const GOOD_WINDOW = 240;

// Per-lane visual identity
const LANE_COLORS = [0x6fd4c4, 0xff6fa1, 0xffd95c, 0x9b6bff] as const;
const LANE_EMOJIS = ['🎵', '⭐', '🎀', '💎'] as const;
const LANE_HEX = ['#6fd4c4', '#ff6fa1', '#ffd95c', '#9b6bff'] as const;

// Key badge circle radius
const BADGE_R = 26;

// Song definitions with BPM — 전체적으로 속도 낮춤 (8살 어린이 기준)
type SongDef = { name: string; emoji: string; bpm: number; bgmKey: 'rhythm' | 'minigame' | 'home' };
const SONGS: SongDef[] = [
  { name: '🌸 쉬운 팝', emoji: '🌸', bpm: 50,  bgmKey: 'home' },     // 매우 쉬움 — 기본 추천
  { name: '👗 패션 비트', emoji: '👗', bpm: 65,  bgmKey: 'rhythm' },  // 쉬움
  { name: '💃 런웨이', emoji: '💃', bpm: 80, bgmKey: 'minigame' },  // 보통
  { name: '⚡ K팝 챌린지', emoji: '⚡', bpm: 95, bgmKey: 'rhythm' },  // 조금 어려움
];

type Note = { gfx: Phaser.GameObjects.Container; lane: number; spawnTime: number };

export class RhythmGameScene extends Phaser.Scene {
  private notes: Note[] = [];
  private score = 0;
  private combo = 0;
  private maxCombo = 0;
  private beatTimer!: Phaser.Time.TimerEvent;
  private endTimer!: Phaser.Time.TimerEvent;
  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private toast!: ToastManager;
  private ended = false;
  private elapsed = 0;
  private progressBar!: Phaser.GameObjects.Rectangle;
  private timeLabel!: Phaser.GameObjects.Text;
  private laneFlashes: (Phaser.GameObjects.Rectangle | null)[] = [null, null, null, null];
  private selectedSong: SongDef = SONGS[0];
  private beatMs = 500; // ms per beat (set from BPM)
  private showingMenu = true;
  private menuContainer: Phaser.GameObjects.Container | null = null;
  private beatCount = 0;

  constructor() { super('RhythmGameScene'); }

  create(): void {
    AudioManager.setScene(this);
    this.cameras.main.setBackgroundColor('#1a0f2e');
    this.toast = new ToastManager(this);
    this.score = 0; this.combo = 0; this.maxCombo = 0;
    this.notes = []; this.ended = false;
    this.showingMenu = true;
    this.elapsed = 0;
    this.beatCount = 0;

    this._buildBackground();
    this._showSongMenu();
  }

  private _buildBackground(): void {
    // Draw lane strips (always visible)
    LANES.forEach((_key, i) => {
      const laneLeft = LANE_X_START + i * LANE_WIDTH;
      const laneCx = laneLeft + LANE_WIDTH / 2;
      this.add.rectangle(laneCx, GAME_HEIGHT / 2, LANE_WIDTH - 4, GAME_HEIGHT, LANE_COLORS[i], 0.07);
      this.add.rectangle(laneCx, GAME_HEIGHT / 2, LANE_WIDTH - 4, GAME_HEIGHT, 0x000000, 0.18);
      if (i < 3) {
        this.add.rectangle(laneLeft + LANE_WIDTH - 1, GAME_HEIGHT / 2, 2, GAME_HEIGHT, 0xffffff, 0.05);
      }
    });
  }

  private _showSongMenu(): void {
    this.menuContainer = this.add.container(0, 0);

    const bg = this.add.graphics();
    bg.fillStyle(0x1a0f2e, 0.92);
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    this.menuContainer.add(bg);

    const title = this.add.text(GAME_WIDTH / 2, 80, '🎵 리듬 게임 — 곡 선택', {
      fontFamily: Theme.font.family,
      fontSize: '32px',
      fontStyle: '900',
      color: '#ffd95c',
    }).setOrigin(0.5);
    this.menuContainer.add(title);

    const sub = this.add.text(GAME_WIDTH / 2, 130, '원하는 곡을 선택해서 시작하세요!', {
      fontFamily: Theme.font.family,
      fontSize: '16px',
      color: '#aaaacc',
    }).setOrigin(0.5);
    this.menuContainer.add(sub);

    SONGS.forEach((song, i) => {
      const x = GAME_WIDTH / 2;
      const y = 230 + i * 110;
      const isDefault = i === 0;

      const cardG = this.add.graphics();
      cardG.fillStyle(isDefault ? 0x2a1a44 : 0x180d36, 1);
      cardG.lineStyle(isDefault ? 3 : 1.5, isDefault ? 0xffd95c : 0x4a3a64, 1);
      cardG.fillRoundedRect(x - 320, y - 40, 640, 80, 14);
      cardG.strokeRoundedRect(x - 320, y - 40, 640, 80, 14);
      this.menuContainer!.add(cardG);

      const bpmMs = Math.round(60000 / song.bpm);
      const diffLabel = song.bpm <= 55 ? '⭐ 매우 쉬움 (추천!)' : song.bpm <= 70 ? '⭐⭐ 쉬움' : song.bpm <= 85 ? '⭐⭐⭐ 보통' : '⭐⭐⭐⭐ 조금 어려움';
      const diffColor = song.bpm <= 55 ? '#6fd4c4' : song.bpm <= 70 ? '#a8f070' : song.bpm <= 85 ? '#ffd95c' : '#ff8c42';

      const emojiText = this.add.text(x - 290, y, song.emoji, { fontSize: '36px' }).setOrigin(0, 0.5);
      const nameText = this.add.text(x - 240, y - 12, song.name, {
        fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '800', color: '#ffffff',
      }).setOrigin(0, 0.5);
      const bpmText = this.add.text(x - 240, y + 14, `${song.bpm} BPM · 비트 ${bpmMs}ms`, {
        fontFamily: Theme.font.family, fontSize: '13px', color: '#aaaacc',
      }).setOrigin(0, 0.5);
      const diffText = this.add.text(x + 250, y, diffLabel, {
        fontFamily: Theme.font.family, fontSize: '16px', fontStyle: '800', color: diffColor,
      }).setOrigin(1, 0.5);

      this.menuContainer!.add([emojiText, nameText, bpmText, diffText]);

      const hit = this.add.rectangle(x, y, 640, 80, 0x000000, 0);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => {
        cardG.clear();
        cardG.fillStyle(0x3a2a54, 1);
        cardG.lineStyle(2.5, 0xff6fa1, 1);
        cardG.fillRoundedRect(x - 320, y - 40, 640, 80, 14);
        cardG.strokeRoundedRect(x - 320, y - 40, 640, 80, 14);
      });
      hit.on('pointerout', () => {
        cardG.clear();
        cardG.fillStyle(isDefault ? 0x2a1a44 : 0x180d36, 1);
        cardG.lineStyle(isDefault ? 3 : 1.5, isDefault ? 0xffd95c : 0x4a3a64, 1);
        cardG.fillRoundedRect(x - 320, y - 40, 640, 80, 14);
        cardG.strokeRoundedRect(x - 320, y - 40, 640, 80, 14);
      });
      hit.on('pointerdown', () => {
        this.selectedSong = song;
        this._startGame();
      });
      this.menuContainer!.add(hit);
    });

    // Back button
    new Button(this, 100, GAME_HEIGHT - 50, '← 뒤로', () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('MiniGameHubScene'));
    }, { width: 120, height: 48, style: 'secondary' });
  }

  private _startGame(): void {
    this.showingMenu = false;
    if (this.menuContainer) { this.menuContainer.destroy(); this.menuContainer = null; }

    // 카운트다운 도중에는 BGM 재생하지 않고 (동기화를 위해)
    // 카운트다운 끝난 직후 BGM과 노트 타이머를 같은 순간에 시작한다.
    AudioManager.stopBgm();
    this.beatMs = Math.round(60000 / this.selectedSong.bpm);
    this.elapsed = 0;
    this.beatCount = 0;

    // ── HUD ────────────────────────────────────────────────────────────────
    this.add.text(GAME_WIDTH / 2, 36, `🎵 ${this.selectedSong.name}`, {
      fontFamily: Theme.font.family,
      fontSize: '22px',
      fontStyle: '800',
      color: Theme.hex.white,
    }).setOrigin(0.5);

    // Score — top-left HUD chip
    const scoreChipG = this.add.graphics();
    scoreChipG.fillStyle(0x2a1a44, 0.9);
    scoreChipG.fillRoundedRect(20, 12, 180, 48, 12);
    this.scoreText = this.add.text(110, 36, '0', {
      fontFamily: Theme.font.family,
      fontSize: '28px',
      fontStyle: '900',
      color: '#ffd95c',
    }).setOrigin(0.5);

    this.comboText = this.add.text(GAME_WIDTH / 2, 70, '', {
      fontFamily: Theme.font.family,
      fontSize: '22px',
      fontStyle: '800',
      color: '#ff6fa1',
    }).setOrigin(0.5);

    // Progress bar
    const barX = 220, barY = 22, barW = GAME_WIDTH - 440, barH = 12;
    const barBg = this.add.graphics();
    barBg.fillStyle(0x333355, 1);
    barBg.fillRoundedRect(barX, barY - barH / 2, barW, barH, 6);
    this.progressBar = this.add.rectangle(barX, barY, barW, barH, 0x44ee88).setOrigin(0, 0.5);
    this.timeLabel = this.add.text(barX + barW + 12, barY, `${SONG_DURATION_MS / 1000}s`, {
      fontFamily: Theme.font.family,
      fontSize: '13px',
      fontStyle: '700',
      color: '#aaaacc',
    }).setOrigin(0, 0.5);

    // ── Hit zone + key badges ──────────────────────────────────────────────
    LANES.forEach((key, i) => {
      const laneCx = LANE_X_START + i * LANE_WIDTH + LANE_WIDTH / 2;

      const flash = this.add.rectangle(laneCx, HIT_LINE_Y - 30, LANE_WIDTH - 6, 100, LANE_COLORS[i], 0);
      this.laneFlashes[i] = flash;

      const hitLine = this.add.graphics();
      hitLine.lineStyle(3, LANE_COLORS[i], 0.9);
      hitLine.lineBetween(
        LANE_X_START + i * LANE_WIDTH + 6, HIT_LINE_Y,
        LANE_X_START + (i + 1) * LANE_WIDTH - 6, HIT_LINE_Y,
      );
      hitLine.lineStyle(12, LANE_COLORS[i], 0.12);
      hitLine.lineBetween(
        LANE_X_START + i * LANE_WIDTH + 6, HIT_LINE_Y,
        LANE_X_START + (i + 1) * LANE_WIDTH - 6, HIT_LINE_Y,
      );

      // Key badge
      const badgeY = HIT_LINE_Y + 48;
      const badgeG = this.add.graphics();
      badgeG.fillStyle(0x2a1a44, 0.9);
      badgeG.fillCircle(laneCx, badgeY, BADGE_R);
      badgeG.lineStyle(2.5, LANE_COLORS[i], 0.9);
      badgeG.strokeCircle(laneCx, badgeY, BADGE_R);
      this.add.text(laneCx, badgeY, key, {
        fontFamily: Theme.font.family,
        fontSize: '22px',
        fontStyle: '900',
        color: LANE_HEX[i],
      }).setOrigin(0.5);

      // Touch/click zone
      const hitZone = this.add.rectangle(laneCx, HIT_LINE_Y, LANE_WIDTH - 4, 200, 0x000000, 0.001);
      hitZone.setInteractive({ useHandCursor: true });
      hitZone.on('pointerdown', () => this.tryHit(i));
    });

    // Keyboard
    this.input.keyboard?.on('keydown-D', () => this.tryHit(0));
    this.input.keyboard?.on('keydown-F', () => this.tryHit(1));
    this.input.keyboard?.on('keydown-J', () => this.tryHit(2));
    this.input.keyboard?.on('keydown-K', () => this.tryHit(3));

    // 3-2-1 카운트다운 후 BGM과 노트 동시 시작 (음악 비트와 정확히 동기화)
    this._showCountdown(() => {
      // BGM 시작 — 곡의 BPM으로 강제 (procedural fallback)
      AudioManager.playBgmProceduralAtBpm(this.selectedSong.bgmKey, this.selectedSong.bpm);
      this._scheduleBeatSpawning();
      this.endTimer = this.time.delayedCall(SONG_DURATION_MS, () => this.end());
    });

    new Button(this, 100, GAME_HEIGHT - 50, '← 포기', () => this.end(), { width: 120, height: 48, style: 'secondary' });

    // Beat pulse indicator on hit line
    this._startBeatPulse();
  }

  /** Hit line pulses on every beat */
  private _startBeatPulse(): void {
    // Draw a pulsing glow on the hit line
    const pulse = this.add.graphics();
    pulse.setDepth(50);
    const updatePulse = () => {
      if (this.ended || this.showingMenu) return;
    };
    // Pulse tween fired every beatMs
    this.time.addEvent({
      delay: this.beatMs,
      loop: true,
      callback: () => {
        if (this.ended || this.showingMenu) return;
        updatePulse();
        pulse.clear();
        pulse.lineStyle(6, 0xffffff, 0.35);
        pulse.lineBetween(LANE_X_START, HIT_LINE_Y, LANE_X_START + LANE_TOTAL_W, HIT_LINE_Y);
        this.tweens.add({
          targets: pulse,
          alpha: 0,
          duration: this.beatMs * 0.7,
          onComplete: () => { pulse.setAlpha(1); },
        });
      },
    });
  }

  /** 3-2-1 → GO! countdown, then calls onDone */
  private _showCountdown(onDone: () => void): void {
    const steps = ['준비...', '3', '2', '1', 'GO! 🎵'];
    let i = 0;
    const showStep = () => {
      if (i >= steps.length) { onDone(); return; }
      const label = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, steps[i], {
        fontFamily: Theme.font.family,
        fontSize: i === 0 ? '40px' : i === steps.length - 1 ? '72px' : '96px',
        fontStyle: '900',
        color: i === steps.length - 1 ? '#ffd95c' : '#ffffff',
        stroke: '#1a0f2e',
        strokeThickness: 6,
      }).setOrigin(0.5).setDepth(200).setAlpha(0).setScale(0.5);

      this.tweens.add({
        targets: label,
        alpha: 1,
        scale: 1,
        duration: 200,
        ease: 'Back.Out',
        onComplete: () => {
          this.tweens.add({
            targets: label,
            alpha: 0,
            scale: 1.3,
            duration: 350,
            delay: 400,
            onComplete: () => { label.destroy(); i++; showStep(); },
          });
        },
      });
    };
    showStep();
  }

  private _scheduleBeatSpawning(): void {
    // Notes should arrive at hit line on the beat.
    // spawn time (game time) = beat_time - NOTE_FALL_DURATION
    // We use a timer that fires every half-beat to determine if a note should spawn

    // Pattern: for each beat, pick 1-2 random lanes
    // Use half-beat subdivisions for variation
    const halfBeat = this.beatMs / 2;

    this.beatTimer = this.time.addEvent({
      delay: halfBeat,
      loop: true,
      callback: () => {
        if (this.ended || this.showingMenu) return;
        this.elapsed += halfBeat;
        this._updateHUD();

        this.beatCount++;
        // Spawn every 2 full beats (slower density) with small off-beat chance at higher BPM
        const isFullBeat = this.beatCount % 2 === 0;
        // Only spawn off-beat at faster songs (bpm>110) with low chance
        const shouldSpawn = isFullBeat || (Math.random() < 0.15 && this.beatMs < 480);

        if (shouldSpawn) {
          // Prefer single lane notes — doubles only at harder BPMs
          const numLanes = (Math.random() < 0.15 && this.beatMs < 500) ? 2 : 1;
          const usedLanes = new Set<number>();
          for (let k = 0; k < numLanes; k++) {
            let lane: number;
            let attempts = 0;
            do {
              lane = Math.floor(Math.random() * 4);
              attempts++;
            } while (usedLanes.has(lane) && attempts < 8);
            usedLanes.add(lane);
            this._spawnBeatNote(lane);
          }
        }
      },
    });
  }

  private _updateHUD(): void {
    const ratio = Math.min(1, this.elapsed / SONG_DURATION_MS);
    const barW = GAME_WIDTH - 440;
    this.progressBar.setDisplaySize(barW * ratio, 12);
    const r = Math.min(255, Math.floor(ratio * 510));
    const g = Math.min(255, Math.floor((1 - ratio) * 510));
    this.progressBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 50));
    const secsLeft = Math.max(0, Math.ceil((SONG_DURATION_MS - this.elapsed) / 1000));
    this.timeLabel.setText(`${secsLeft}s`);
  }

  update(_time: number, delta: number): void {
    if (this.ended || this.showingMenu) return;

    // Pulse notes as they approach the hit line
    for (const n of this.notes) {
      if (!n.gfx.active) continue;
      const dist = Math.abs(n.gfx.y - HIT_LINE_Y);
      const pulseScale = dist < 100 ? 1 + 0.15 * (1 - dist / 100) : 1;
      n.gfx.setScale(pulseScale);
    }
    void delta;
  }

  /** Draw a shaped note graphic for the given lane */
  private _makeNoteGfx(lane: number): Phaser.GameObjects.Container {
    const col = LANE_COLORS[lane];
    const emoji = LANE_EMOJIS[lane];
    const g = this.add.graphics();
    const container = this.add.container(0, 0, [g]);

    if (lane === 0) {
      g.fillStyle(col, 1);
      g.fillCircle(0, 0, 28);
      g.fillStyle(0xffffff, 0.25);
      g.fillCircle(0, 0, 18);
      g.lineStyle(3, 0xffffff, 0.8);
      g.strokeCircle(0, 0, 28);
    } else if (lane === 1) {
      g.fillStyle(col, 1);
      g.lineStyle(2, 0xffffff, 0.8);
      const pts: number[] = [];
      for (let j = 0; j < 10; j++) {
        const a = (j * Math.PI) / 5 - Math.PI / 2;
        const rv = j % 2 === 0 ? 28 : 12;
        pts.push(Math.cos(a) * rv, Math.sin(a) * rv);
      }
      g.fillPoints(pts.reduce<Phaser.Geom.Point[]>((acc, v, idx) => {
        if (idx % 2 === 0) acc.push(new Phaser.Geom.Point(v, pts[idx + 1]));
        return acc;
      }, []), true);
    } else if (lane === 2) {
      g.fillStyle(col, 1);
      g.lineStyle(2, 0xffffff, 0.8);
      g.fillTriangle(0, -30, 26, 0, 0, 30);
      g.fillTriangle(0, -30, -26, 0, 0, 30);
    } else {
      g.fillStyle(col, 1);
      g.lineStyle(2, 0xffffff, 0.8);
      const hexPts: Phaser.Geom.Point[] = [];
      for (let j = 0; j < 6; j++) {
        const a = (j * Math.PI) / 3 - Math.PI / 6;
        hexPts.push(new Phaser.Geom.Point(Math.cos(a) * 28, Math.sin(a) * 28));
      }
      g.fillPoints(hexPts, true);
      g.fillStyle(0xffffff, 0.2);
      g.fillTriangle(-10, -18, 10, -18, 0, 0);
    }

    const emojiText = this.add.text(0, 0, emoji, { fontSize: '16px' }).setOrigin(0.5);
    container.add(emojiText);

    const glow = this.add.graphics();
    glow.fillStyle(col, 0.15);
    glow.fillCircle(0, 0, 38);
    container.addAt(glow, 0);

    return container;
  }

  private _spawnBeatNote(lane: number): void {
    const laneCx = LANE_X_START + lane * LANE_WIDTH + LANE_WIDTH / 2;

    const noteGfx = this._makeNoteGfx(lane);
    // Start above screen — will arrive at HIT_LINE_Y after NOTE_FALL_DURATION
    const startY = -40;
    noteGfx.setPosition(laneCx, startY);

    const note: Note = { gfx: noteGfx, lane, spawnTime: this.time.now };
    this.notes.push(note);

    // Travel from startY to HIT_LINE_Y in exactly NOTE_FALL_DURATION
    const travelDistance = HIT_LINE_Y - startY;
    this.tweens.add({
      targets: noteGfx,
      y: HIT_LINE_Y + travelDistance * 0.15, // overshoot a bit past hit line before destroying
      duration: NOTE_FALL_DURATION * 1.15,
      onComplete: () => {
        if (noteGfx.active) {
          this._showMissFlash(lane, laneCx);
          noteGfx.destroy();
          this.notes = this.notes.filter(n => n !== note);
          this.combo = 0;
          this.comboText.setText('');
        }
      },
    });
  }

  private _showMissFlash(lane: number, x: number): void {
    const missX = this.add.text(x, HIT_LINE_Y, '✕', {
      fontFamily: Theme.font.family,
      fontSize: '38px',
      fontStyle: '900',
      color: '#ff3333',
    }).setOrigin(0.5).setDepth(100);
    this.tweens.add({
      targets: missX,
      alpha: 0,
      y: HIT_LINE_Y - 30,
      duration: 500,
      onComplete: () => missX.destroy(),
    });
    const f = this.laneFlashes[lane];
    if (f) {
      f.setFillStyle(0xff3333, 0.35);
      this.tweens.add({ targets: f, alpha: 0, duration: 300, onComplete: () => f.setAlpha(0) });
    }
  }

  private _flashLanePress(lane: number): void {
    const f = this.laneFlashes[lane];
    if (!f) return;
    this.tweens.killTweensOf(f);
    f.setFillStyle(LANE_COLORS[lane], 0.75);
    f.setAlpha(0.85);
    this.tweens.add({ targets: f, alpha: 0, duration: 280, ease: 'Cubic.Out', onComplete: () => f.setAlpha(0) });

    // 키 누른 위치 플래시 — 짧고 강한 반짝임
    const laneCx = LANE_X_START + lane * LANE_WIDTH + LANE_WIDTH / 2;
    const ping = this.add.circle(laneCx, HIT_LINE_Y, 30, LANE_COLORS[lane], 0.7).setDepth(190);
    this.tweens.add({
      targets: ping,
      scaleX: 2.5, scaleY: 2.5, alpha: 0,
      duration: 280, ease: 'Cubic.Out',
      onComplete: () => ping.destroy(),
    });
  }

  private _spawnHitParticles(x: number, y: number, lane: number, isPerfect: boolean): void {
    const laneCol = LANE_COLORS[lane];

    // 1) 큰 동심원 충격파 — 두세 겹
    for (let r = 0; r < (isPerfect ? 3 : 2); r++) {
      const ring = this.add.circle(x, y, 8, laneCol, 0).setStrokeStyle(4 - r, laneCol, 0.95).setDepth(195);
      this.tweens.add({
        targets: ring,
        radius: isPerfect ? 130 + r * 25 : 80 + r * 15,
        alpha: 0,
        duration: isPerfect ? 600 : 380,
        delay: r * 60,
        ease: 'Cubic.Out',
        onUpdate: () => { /* circle radius re-applied via setRadius below */ },
        onComplete: () => ring.destroy(),
      });
      // setRadius 트윈을 위해 매 프레임 갱신
      this.tweens.add({
        targets: ring,
        scaleX: isPerfect ? 16 : 10,
        scaleY: isPerfect ? 16 : 10,
        duration: isPerfect ? 600 : 380,
        delay: r * 60,
        ease: 'Cubic.Out',
      });
    }

    // 2) 별/하트/다이아 모양 파티클 사방으로 분출
    const emojis = isPerfect ? ['✨', '💖', '⭐', '💫', '🌟', '💎'] : ['✨', '💫', '⭐'];
    const count = isPerfect ? 18 : 10;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const dist = (isPerfect ? 90 : 55) + Math.random() * 60;
      const emoji = emojis[Math.floor(Math.random() * emojis.length)];
      const spark = this.add.text(x, y, emoji, {
        fontSize: `${isPerfect ? 22 + Math.random() * 14 : 14 + Math.random() * 8}px`,
      }).setOrigin(0.5).setDepth(202);
      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist - (isPerfect ? 30 : 0),
        alpha: 0,
        scale: isPerfect ? 1.8 : 1.0,
        angle: (Math.random() - 0.5) * 720,
        duration: isPerfect ? 700 + Math.random() * 200 : 420 + Math.random() * 120,
        delay: Math.random() * 80,
        ease: 'Cubic.Out',
        onComplete: () => spark.destroy(),
      });
    }

    // 3) 색깔 파티클 (작은 사각형 폭발)
    const colors = [laneCol, 0xffffff, 0xffd95c, 0xff6fa1];
    const sqCount = isPerfect ? 14 : 8;
    for (let i = 0; i < sqCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * 80;
      const col = colors[Math.floor(Math.random() * colors.length)];
      const sq = this.add.rectangle(x, y, 6 + Math.random() * 6, 6 + Math.random() * 6, col).setDepth(201);
      this.tweens.add({
        targets: sq,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist + (isPerfect ? -30 : 0),
        alpha: 0,
        scale: 0.2,
        angle: (Math.random() - 0.5) * 540,
        duration: 500 + Math.random() * 300,
        ease: 'Cubic.Out',
        onComplete: () => sq.destroy(),
      });
    }

    // 4) PERFECT 전용: 화면 전체 골드 플래시
    if (isPerfect) {
      const flash = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xfff8d8, 0.35).setDepth(180);
      this.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 220,
        ease: 'Cubic.Out',
        onComplete: () => flash.destroy(),
      });
      // 가벼운 화면 흔들림
      this.cameras.main.shake(140, 0.006);
      // 레인에 빛기둥
      const beam = this.add.rectangle(x, GAME_HEIGHT / 2, LANE_WIDTH - 8, GAME_HEIGHT, laneCol, 0.4).setDepth(185);
      this.tweens.add({
        targets: beam,
        alpha: 0,
        scaleX: 1.5,
        duration: 360,
        ease: 'Cubic.Out',
        onComplete: () => beam.destroy(),
      });
    } else {
      this.cameras.main.shake(60, 0.003);
    }
  }

  private tryHit(lane: number): void {
    if (this.ended || this.showingMenu) return;
    this._flashLanePress(lane);

    const candidates = this.notes.filter(n => n.lane === lane && Math.abs(n.gfx.y - HIT_LINE_Y) < 200);
    if (candidates.length === 0) return;
    const note = candidates.reduce((a, b) => Math.abs(a.gfx.y - HIT_LINE_Y) < Math.abs(b.gfx.y - HIT_LINE_Y) ? a : b);
    const dist = Math.abs(note.gfx.y - HIT_LINE_Y);
    let pts = 0; let label = '';
    if (dist < PERFECT_WINDOW) { pts = 30; label = 'PERFECT!'; }
    else if (dist < GOOD_WINDOW) { pts = 15; label = 'GOOD'; }
    if (pts > 0) {
      AudioManager.playSfx('beat_hit');
      this.score += pts + this.combo;
      this.combo++; this.maxCombo = Math.max(this.maxCombo, this.combo);
      this.scoreText.setText(`${this.score}`);
      this.comboText.setText(`${this.combo} 콤보!`);
      const isPerfect = pts === 30;
      const fx = this.add.text(note.gfx.x, HIT_LINE_Y - 50, label, {
        fontFamily: Theme.font.family,
        fontSize: isPerfect ? '40px' : '24px',
        fontStyle: '900',
        color: isPerfect ? '#ffd95c' : '#ff6fa1',
        stroke: isPerfect ? '#5a3000' : '#1a0a18',
        strokeThickness: isPerfect ? 5 : 3,
      }).setOrigin(0.5).setDepth(220).setScale(0.3);
      this.tweens.add({
        targets: fx,
        scaleX: isPerfect ? 1.3 : 1.0,
        scaleY: isPerfect ? 1.3 : 1.0,
        duration: 180,
        ease: 'Back.Out',
      });
      this.tweens.add({
        targets: fx,
        alpha: 0,
        y: fx.y - 70,
        scaleX: isPerfect ? 1.6 : 1.2,
        scaleY: isPerfect ? 1.6 : 1.2,
        duration: 800,
        delay: 250,
        ease: 'Cubic.Out',
        onComplete: () => fx.destroy(),
      });
      // 콤보 텍스트도 살짝 펄스
      this.tweens.killTweensOf(this.comboText);
      this.comboText.setScale(1);
      this.tweens.add({
        targets: this.comboText,
        scaleX: isPerfect ? 1.5 : 1.2,
        scaleY: isPerfect ? 1.5 : 1.2,
        duration: 100,
        yoyo: true,
        ease: 'Back.Out',
      });
      this._spawnHitParticles(note.gfx.x, HIT_LINE_Y, lane, isPerfect);
      note.gfx.destroy();
      this.notes = this.notes.filter(n => n !== note);
    }
  }

  private end(): void {
    if (this.ended) return;
    this.ended = true;
    if (this.beatTimer) this.beatTimer.remove();
    if (this.endTimer) this.endTimer.remove();
    const reward = MiniGameRunner.reportResult('rhythm', this.score, GAME_WIDTH / 2, GAME_HEIGHT / 2);
    AudioManager.playSfx(this.score > 0 ? 'win' : 'lose');
    this.toast.show(`🎉 ${this.score}점 / 최대 ${this.maxCombo} 콤보! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
    this.time.delayedCall(2000, () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('MiniGameHubScene'));
    });
  }
}
